import { useState, useEffect, useMemo } from 'react';
import * as Sentry from "@sentry/react";
import { addDoc, collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { syncNormalizedTags } from '../lib/tags';
import { IS_MOCK_DEMO, NOTE_STATUS } from '../constants';
import { isOfflineError } from '../lib/firestore';
import { mockNotes } from '../mockData';

function normalizeSummaryCompleteness(value) {
  if (value === 'partial') return 'partial';
  return 'complete';
}

export function useNotes(user, globalSelectedTags = [], insights = []) {
  const [notes, setNotes] = useState(() => (IS_MOCK_DEMO ? mockNotes.map(n => ({
    ...n,
    title: n.title ?? '',
    summary: n.summary ?? '',
    body: n.body ?? n.text ?? '',
    type: n.type ?? 'chat_summary',
    tags: n.tags ?? [],
    sourceRoomIds: n.sourceRoomIds ?? [],
    sourceMessageIds: n.sourceMessageIds ?? [],
    metadata: {
      ...n.metadata,
      summaryCompleteness: normalizeSummaryCompleteness(n.metadata?.summaryCompleteness),
      revisionCount: n.metadata?.revisionCount ?? 0,
    },
  })) : []));
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [noteSearchText, setNoteSearchText] = useState('');
  const [selectedNoteFilter, setSelectedNoteFilter] = useState('all'); // 'all', 'unextracted', 'extracted'

  useEffect(() => {
    if (IS_MOCK_DEMO || !user?.uid) return undefined;
    const notesQuery = query(
      collection(db, 'notes'),
      where('ownerUid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(notesQuery, (snapshot) => {
      setNotes(snapshot.docs
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            title: data.title ?? '',
            summary: data.summary ?? '',
            body: data.body ?? data.text ?? '',
            type: data.type ?? 'chat_summary',
            tags: data.tags ?? [],
            sourceRoomIds: data.sourceRoomIds ?? [],
            sourceMessageIds: data.sourceMessageIds ?? [],
            metadata: {
              ...data.metadata,
              summaryCompleteness: normalizeSummaryCompleteness(data.metadata?.summaryCompleteness),
              revisionCount: data.metadata?.revisionCount ?? 0,
            },
          };
        })
        .filter((note) => note.type === 'chat_summary' || note.type === 'manual_note'));
    }, (error) => {
      console.error('Failed to subscribe notes:', error);
      if (!isOfflineError(error)) {
        Sentry.captureException(error, { tags: { action: 'useNotes_onSnapshot' } });
        alert('メモ一覧のリアルタイム同期に失敗しました。Firestoreのルールを確認してください。');
      }
    });
  }, [user?.uid]);

  const selectedNote = useMemo(() => (
    notes.find((note) => note.id === selectedNoteId) || null
  ), [notes, selectedNoteId]);

  const filteredNotes = useMemo(() => {
    // Optimization: Pre-calculate a Set of extracted note IDs to reduce complexity from O(N*I) to O(N+I)
    const extractedNoteIds = new Set();
    if (insights) {
      insights.forEach(insight => {
        if (insight.sourceNoteIds) {
          insight.sourceNoteIds.forEach(id => extractedNoteIds.add(id));
        }
      });
    }

    return notes.filter((note) => {
      const search = noteSearchText.trim().toLowerCase();
      const searchTarget = [
        note.title,
        note.summary,
        note.body,
        ...(note.tags || []),
      ].filter(Boolean).join(' ').toLowerCase();
      const matchesTag = globalSelectedTags.length === 0 || globalSelectedTags.some((tag) => (note.tags || []).includes(tag));
      const matchesSearch = !search || searchTarget.includes(search);
      
      const isExtracted = extractedNoteIds.has(note.id);
      let matchesFilter = selectedNoteFilter === 'all';
      if (!matchesFilter) {
        if (selectedNoteFilter === 'extracted') {
          matchesFilter = isExtracted;
        } else if (selectedNoteFilter === 'unextracted') {
          matchesFilter = !isExtracted;
        }
      }

      return matchesTag && matchesSearch && matchesFilter;
    });
  }, [notes, noteSearchText, globalSelectedTags, selectedNoteFilter, insights]);

  const notesForSelectedTag = useMemo(() => (
    globalSelectedTags.length > 0
      ? notes.filter((note) => globalSelectedTags.some((tag) => (note.tags || []).includes(tag)))
      : []
  ), [notes, globalSelectedTags]);

  const handleUpdateNoteTags = async (noteId, tags) => {
    const note = notes.find(n => n.id === noteId);
    const nextRevisionCount = (note?.metadata?.revisionCount || 0) + 1;
    const previousTags = note?.tags || [];
    if (IS_MOCK_DEMO) {
      setNotes((current) => current.map((note) => (
        note.id === noteId ? {
          ...note,
          tags,
          updatedAt: new Date().toISOString(),
          metadata: {
            ...note.metadata,
            revisionType: 'user_edit',
            revisionCount: nextRevisionCount,
            lastRevisedAt: new Date().toISOString(),
          }
        } : note
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'notes', noteId), {
        tags,
        updatedAt: serverTimestamp(),
        'metadata.revisionType': 'user_edit',
        'metadata.revisionCount': nextRevisionCount,
        'metadata.lastRevisedAt': serverTimestamp(),
      });
      await syncNormalizedTags(db, user?.uid, noteId, 'note', tags, previousTags);
    } catch (error) {
      console.error('Failed to update note tags:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateNoteTags' }, extra: { noteId } });
      throw error;
    }
  };

  const handleUpdateNoteBody = async (noteId, body) => {
    const note = notes.find(n => n.id === noteId);
    const nextRevisionCount = (note?.metadata?.revisionCount || 0) + 1;
    if (IS_MOCK_DEMO) {
      setNotes((current) => current.map((note) => (
        note.id === noteId ? {
          ...note,
          body,
          updatedAt: new Date().toISOString(),
          metadata: {
            ...note.metadata,
            revisionType: 'user_edit',
            revisionCount: nextRevisionCount,
            lastRevisedAt: new Date().toISOString(),
          }
        } : note
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'notes', noteId), {
        body,
        updatedAt: serverTimestamp(),
        'metadata.revisionType': 'user_edit',
        'metadata.revisionCount': nextRevisionCount,
        'metadata.lastRevisedAt': serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to update note body:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateNoteBody' }, extra: { noteId } });
      throw error;
    }
  };

  const handleCreateNote = async (noteData) => {
    const newNote = {
      ...noteData,
      ownerUid: user?.uid,
      type: 'manual_note',
      status: NOTE_STATUS.INBOX,
      sourceRoomId: null,
      sourceMessageIds: [],
      createdAt: IS_MOCK_DEMO ? new Date().toISOString() : serverTimestamp(),
      updatedAt: IS_MOCK_DEMO ? new Date().toISOString() : serverTimestamp(),
      metadata: {
        ...(noteData.metadata || {}),
        revisionCount: 0,
      }
    };

    if (IS_MOCK_DEMO) {
      const mockId = `mock-note-${Date.now()}`;
      setNotes((current) => [{ id: mockId, ...newNote }, ...current]);
      return { id: mockId, ...newNote };
    }

    try {
      const docRef = await addDoc(collection(db, 'notes'), newNote);
      return { id: docRef.id, ...newNote };
    } catch (error) {
      console.error('Failed to create note:', error);
      Sentry.captureException(error, { tags: { action: 'handleCreateNote' } });
      throw error;
    }
  };

  return {
    notes,
    setNotes,
    selectedNoteId,
    setSelectedNoteId,
    noteSearchText,
    setNoteSearchText,
    selectedNoteFilter,
    setSelectedNoteFilter,
    selectedNote,
    filteredNotes,
    notesForSelectedTag,
    handleUpdateNoteTags,
    handleUpdateNoteBody,
    handleCreateNote,
    handleUpdateNoteStatus: async (noteId, status) => {
      if (IS_MOCK_DEMO) {
        setNotes((current) => current.map((note) => (
          note.id === noteId ? { ...note, status, updatedAt: new Date().toISOString() } : note
        )));
        return;
      }
      try {
        await updateDoc(doc(db, 'notes', noteId), { status, updatedAt: serverTimestamp() });
      } catch (error) {
        console.error('Failed to update note status:', error);
        Sentry.captureException(error, { tags: { action: 'handleUpdateNoteStatus' }, extra: { noteId } });
      }
    },
  };
}
